import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Sparkles, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Logga in — Inkubera Portfolio Intelligence" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/oversikt" });
  }, [loading, user, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) {
      setError("Fel e-post eller lösenord.");
      return;
    }
    navigate({ to: "/oversikt" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-4 sm:px-8 py-4 sm:py-6">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <div className="size-9 rounded-xl bg-foreground flex items-center justify-center text-background">
            <Sparkles className="size-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-base sm:text-lg leading-none">Inkubera</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              Portfolio Intelligence
            </div>
          </div>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-6 sm:py-10">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-8">
            <div className="size-10 rounded-xl bg-secondary flex items-center justify-center mb-5">
              <Lock className="size-4" />
            </div>
            <h1 className="font-display text-2xl">Logga in</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Endast behöriga användare har åtkomst.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">E-post</label>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Lösenord</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
                />
              </div>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full h-10 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? "Loggar in…" : "Logga in"}
              </button>
            </form>

            <div className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                Glömt lösenord?
              </Link>
            </div>

            <p className="mt-6 text-xs text-muted-foreground text-center">
              Behöver du ett konto? Kontakta din administratör för att bli inbjuden.
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
              ← Tillbaka
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
